import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemDetail } from '@up2d8/api-client';
import {
  BRAND_DEFAULTS,
  BRAND_LIMITS,
  type PlatformBrand,
  type PlatformBrandSettings,
  type UpdatePlatformBrandRequest,
} from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { setPlatformTheme } from '../../shared/theme';
import {
  AdminIcon,
  AdminPage,
  BrandShield,
  Btn,
  Card,
  CardHead,
  CardPad,
  Field,
  Input,
  Note,
  PageHead,
  Textarea,
  useAdminToast,
} from '../admin-kit';
import {
  ColoursCard,
  EmailCard,
  InheritHint,
  LoginCard,
  RESET_ALL,
  ResetAllModal,
  TypeCard,
  draftFrom,
  draftRequest,
  previewOf,
  up2d8Defaults,
  type BrandDraft,
} from './brand-form';
import {
  BACKGROUND_ACCEPT,
  BITMAP_ACCEPT,
  BrandImageField,
  LOGO_ACCEPT,
} from './brand-image-field';
import { BrandPreview } from './brand-preview';
import './huisstijl.css';

const QUERY_KEY = ['platform-brand'] as const;

/** Het formulier van Merk: de kaarten van Huisstijl plus de productnaam en de voettekst. */
interface PlatformDraft extends BrandDraft {
  productName: string;
  emailFooter: string;
}

function platformDraft(settings: PlatformBrandSettings): PlatformDraft {
  return {
    ...draftFrom(settings, up2d8Defaults(settings.productName ?? BRAND_DEFAULTS.productName)),
    productName: settings.productName ?? '',
    emailFooter: settings.emailFooter ?? '',
  };
}

/**
 * Het merk van het product (`/master/merk`, MRK-3, ADR-0084).
 *
 * Dezelfde kaarten als Huisstijl, met daarboven wat alleen het platform kiest: de productnaam, het
 * productlogo en het favicon, en in de e-mailkaart de voettekst. Wat hier leeg blijft, is de
 * standaard van UP2D8; wat hier gekozen wordt, erft elk bedrijf dat zelf niets koos.
 *
 * Alleen voor het platformbeheer met `tenants.brand.manage`. De route staat al achter
 * `aud=platform`; de API weigert zonder het recht, en het scherm zegt dat dan meteen.
 */
export function PlatformBrandPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useAdminToast();
  const queryClient = useQueryClient();
  const allowed = has('tenants.brand.manage');

  const brand = useQuery({
    queryKey: QUERY_KEY,
    enabled: allowed,
    queryFn: async (): Promise<PlatformBrand> => {
      const { data, error } = await api.GET('/api/v1/platform/brand');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PlatformBrand;
    },
  });

  const [draft, setDraft] = useState<PlatformDraft | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  // Op de keuzes, niet op het hele antwoord: een nieuw logo mag niet wissen wat nog niet bewaard is.
  const settingsKey = brand.data ? JSON.stringify(brand.data.settings) : null;
  useEffect(() => {
    if (!settingsKey) return;
    setDraft(platformDraft(JSON.parse(settingsKey) as PlatformBrandSettings));
  }, [settingsKey]);

  const toepassen = (next: PlatformBrand): void => {
    queryClient.setQueryData(QUERY_KEY, next);
    // Meteen op de schil, zodat de zijbalk het zonder herladen volgt; en de publieke query
    // opnieuw, want daar leest het aanmeldscherm (en de cache in localStorage) uit.
    setPlatformTheme(next.theme);
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['platform-brand-public'] });
  };

  const save = useMutation({
    mutationFn: async (input: UpdatePlatformBrandRequest) => {
      const { data, error } = await api.PUT('/api/v1/platform/brand', { body: input });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as PlatformBrand;
    },
    onMutate: () => setFout(null),
    onSuccess: (next) => {
      toepassen(next);
      toast.show(t('beheer.merk.platform.saved'));
    },
    onError: (error) => setFout(problemDetail(error) ?? t('beheer.merk.platform.failed')),
  });

  const resetAll = useMutation({
    mutationFn: async (withLogos: boolean) => {
      const reset = await api.PUT('/api/v1/platform/brand', {
        body: { ...RESET_ALL, productName: null, emailFooter: null },
      });
      if (reset.error || !reset.data) throw reset.error ?? new Error('Opslaan mislukt');
      let latest = reset.data as PlatformBrand;
      const leeg = { body: { fileId: null } };
      const paden = [
        [latest.theme.loginBackgroundUrl, '/api/v1/platform/brand/login-background'],
        ...(withLogos
          ? ([
              [latest.theme.productLogoUrl, '/api/v1/platform/brand/logo'],
              [latest.theme.productLogoDarkUrl, '/api/v1/platform/brand/logo-dark'],
              [latest.theme.faviconUrl, '/api/v1/platform/brand/favicon'],
            ] as const)
          : []),
      ] as const;
      for (const [set, path] of paden) {
        if (!set) continue;
        const { data, error } = await api.PUT(path, leeg);
        if (error || !data) throw error ?? new Error('Opslaan mislukt');
        latest = data as PlatformBrand;
      }
      return latest;
    },
    onSuccess: (next) => {
      setResetOpen(false);
      toepassen(next);
      toast.show(t('beheer.merk.resetAllDone'));
    },
  });

  const head = (
    <PageHead title={t('beheer.merk.platform.title')} sub={t('beheer.merk.platform.sub')} />
  );

  if (!allowed) {
    return (
      <AdminPage>
        {head}
        <Note tone="attention" role="alert" testId="platform-brand-no-right">
          {t('beheer.merk.platform.noRight')}
        </Note>
      </AdminPage>
    );
  }

  if (!brand.data || !draft) {
    return (
      <AdminPage>
        {head}
        {brand.isError ? (
          <Note tone="danger" role="alert">
            {problemDetail(brand.error) ?? t('errors.generic')}
          </Note>
        ) : (
          <LoadingState rows={5} label={t('common.loading')} />
        )}
      </AdminPage>
    );
  }

  const current = brand.data;
  const own = current.settings;
  const productName = draft.productName.trim() || BRAND_DEFAULTS.productName;
  const inherited = up2d8Defaults(productName);
  const preview = previewOf(draft, inherited);
  const shield = (
    <span className="a-orgtile" style={{ background: preview.primary, color: preview.onPrimary }}>
      <BrandShield />
    </span>
  );
  const attach =
    (
      path:
        | '/api/v1/platform/brand/logo'
        | '/api/v1/platform/brand/logo-dark'
        | '/api/v1/platform/brand/favicon'
        | '/api/v1/platform/brand/login-background',
    ) =>
    async (fileId: string | null): Promise<void> => {
      const { data, error } = await api.PUT(path, { body: { fileId } });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      toepassen(data as PlatformBrand);
    };

  return (
    <AdminPage>
      <PageHead
        title={t('beheer.merk.platform.title')}
        sub={t('beheer.merk.platform.sub')}
        actions={
          <>
            <Btn variant="ghost" testId="reset-all" onClick={() => setResetOpen(true)}>
              {t('beheer.merk.platform.resetAll', { name: BRAND_DEFAULTS.productName })}
            </Btn>
            <Btn
              icon={<AdminIcon name="check" size={16} />}
              disabled={save.isPending}
              testId="save-platform-brand"
              onClick={() =>
                save.mutate({
                  ...draftRequest(draft),
                  productName: draft.productName.trim() || null,
                  emailFooter: draft.emailFooter.trim() || null,
                })
              }
            >
              {t('beheer.merk.platform.save')}
            </Btn>
          </>
        }
      />

      <div className="a-grid a-brand-grid">
        <div className="a-stack">
          <Card testId="product-card">
            <CardHead title={t('beheer.merk.platform.product')} />
            <CardPad>
              <Field
                label={t('beheer.merk.platform.productName')}
                hint={
                  <InheritHint
                    level="platform"
                    own={own.productName !== null}
                    value={BRAND_DEFAULTS.productName}
                  >
                    {t('beheer.merk.counter', {
                      count: draft.productName.length,
                      max: BRAND_LIMITS.productName,
                    })}{' '}
                    · {t('beheer.merk.platform.productNameHint')}
                  </InheritHint>
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    value={draft.productName}
                    maxLength={BRAND_LIMITS.productName}
                    placeholder={BRAND_DEFAULTS.productName}
                    data-testid="product-name-input"
                    onChange={(event) => setDraft({ ...draft, productName: event.target.value })}
                  />
                )}
              </Field>
            </CardPad>
          </Card>

          <Card>
            <CardHead title={t('beheer.merk.platform.images')} />
            <CardPad>
              <div className="a-grid three">
                <BrandImageField
                  kind="product-logo"
                  label={t('beheer.merk.platform.logoLight')}
                  hint={t('beheer.merk.logoLightHint')}
                  url={current.theme.productLogoUrl}
                  fallback={shield}
                  accept={LOGO_ACCEPT}
                  purpose="platform_logo"
                  attach={attach('/api/v1/platform/brand/logo')}
                  onSaved={toast.show}
                />
                <BrandImageField
                  kind="product-logo-dark"
                  label={t('beheer.merk.platform.logoDark')}
                  hint={t('beheer.merk.logoDarkHint')}
                  url={current.theme.productLogoDarkUrl}
                  fallback={shield}
                  dark
                  accept={LOGO_ACCEPT}
                  purpose="platform_logo"
                  attach={attach('/api/v1/platform/brand/logo-dark')}
                  onSaved={toast.show}
                />
                <BrandImageField
                  kind="favicon"
                  label={t('beheer.merk.platform.favicon')}
                  hint={t('beheer.merk.platform.faviconHint')}
                  url={current.theme.faviconUrl}
                  fallback={shield}
                  accept={BITMAP_ACCEPT}
                  purpose="favicon"
                  attach={attach('/api/v1/platform/brand/favicon')}
                  onSaved={toast.show}
                />
              </div>
            </CardPad>
          </Card>

          <ColoursCard
            level="platform"
            draft={draft}
            onChange={(next) => setDraft({ ...draft, ...next })}
            own={own}
            inherited={inherited}
            error={fout}
            reset={
              <Btn
                variant="ghost"
                size="sm"
                disabled={save.isPending}
                testId="reset-branding"
                onClick={() => save.mutate({ primary: null, accent: null })}
              >
                {t('beheer.merk.resetTo', { name: BRAND_DEFAULTS.productName })}
              </Btn>
            }
          />
          <TypeCard
            level="platform"
            draft={draft}
            onChange={(next) => setDraft({ ...draft, ...next })}
            own={own}
            inherited={inherited}
          />
          <LoginCard
            level="platform"
            draft={draft}
            onChange={(next) => setDraft({ ...draft, ...next })}
            own={own}
            inherited={inherited}
            background={
              <BrandImageField
                kind="login-background"
                label={t('beheer.merk.loginBackground')}
                hint={t('beheer.merk.loginBackgroundHint')}
                url={current.theme.loginBackgroundUrl}
                fallback={<span className="a-hint">{t('beheer.merk.noImage')}</span>}
                dark
                accept={BACKGROUND_ACCEPT}
                purpose="login_background"
                attach={attach('/api/v1/platform/brand/login-background')}
                onSaved={toast.show}
              />
            }
          />
          <EmailCard
            level="platform"
            draft={draft}
            onChange={(next) => setDraft({ ...draft, ...next })}
            own={own}
            inherited={inherited}
          >
            <Field
              label={t('beheer.merk.footer')}
              hint={
                <>
                  {t('beheer.merk.counter', {
                    count: draft.emailFooter.length,
                    max: BRAND_LIMITS.emailFooter,
                  })}{' '}
                  · {t('beheer.merk.footerHint')}
                </>
              }
            >
              {(props) => (
                <Textarea
                  {...props}
                  value={draft.emailFooter}
                  maxLength={BRAND_LIMITS.emailFooter}
                  data-testid="email-footer"
                  onChange={(event) => setDraft({ ...draft, emailFooter: event.target.value })}
                />
              )}
            </Field>
          </EmailCard>
        </div>

        <div className="a-stack a-brand-side">
          <BrandPreview
            brand={preview}
            name={productName}
            logoUrl={current.theme.productLogoUrl}
            logoDarkUrl={current.theme.productLogoDarkUrl}
            loginBackgroundUrl={current.theme.loginBackgroundUrl}
            showLogin
          />
          <h2 className="a-sect-title">{t('beheer.merk.platform.previewMail')}</h2>
          <div className="a-phone-card" data-testid="mail-preview">
            <div className="a-name">{draft.emailSenderName.trim() || productName}</div>
            {draft.emailFooter.trim() ? (
              <div className="a-meta" style={{ whiteSpace: 'pre-line' }}>
                {draft.emailFooter.trim()}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ResetAllModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title={t('beheer.merk.platform.resetAllTitle', { name: BRAND_DEFAULTS.productName })}
        text={t('beheer.merk.platform.resetAllText', { name: BRAND_DEFAULTS.productName })}
        logosLabel={t('beheer.merk.platform.resetAllLogos')}
        busy={resetAll.isPending}
        error={
          resetAll.isError
            ? (problemDetail(resetAll.error) ?? t('beheer.merk.platform.failed'))
            : null
        }
        onConfirm={(withLogos) => resetAll.mutate(withLogos)}
      />
      {toast.element}
    </AdminPage>
  );
}
